import {
  useAeropayUser,
  useSelectAeropayBankAccount,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { JaneGoldTag } from '@iheartjane/menu';
import {
  Box,
  Button,
  EditIcon,
  Flex,
  JanePayLogo,
  Link,
  Skeleton,
  Typography,
  useToast,
  VerifiedIcon,
} from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import { useEffect, useState } from 'react';

import {
  BankLinkingModal,
  SelectBankAccountModal,
} from '@jane/shared-ecomm/components';
import { useCheckout } from '@jane/shared-ecomm/providers';
import {
  trackChangeBankAccountSuccess,
  trackChangeBankAccountTap,
} from '@jane/shared-ecomm/tracking';
import { getJwt } from '@jane/shared/auth';
import { useJaneUser } from '@jane/shared/data-access';
import { useShouldShowGold } from '@jane/shared/hooks';

import styles from './JanePayHeader.module.css';

export const JanePayHeader = () => {
  const payByBankUI = useFlag(FLAGS.c3PayByBankUI);
  //TODO: implement this UI variant in Phase 2 of Jane Pay checkout experiment
  const trustedByUI = false;

  const [openSelectBankModal, setOpenSelectBankModal] = useState(false);
  const [openBankLinkingModal, setOpenBankLinkingModal] = useState(false);
  const [addNewAccount, setAddNewAccount] = useState(false);
  const toast = useToast();
  const {
    cart: { brand_discounts_total },
    store: { id, jane_pay_integration, photo, state },
  } = useCheckout();
  const shouldShowGold = useShouldShowGold({ store: { id, state } });

  const { data: janeUserData } = useJaneUser();
  const jwt = getJwt();
  const { data: aeropayUserData, isFetching } = useAeropayUser({
    janeUserId: janeUserData?.user?.id || null,
    jwt,
  });

  const {
    mutate: selectAeropayBankAccount,
    isPending,
    isError,
  } = useSelectAeropayBankAccount({
    janeUserId: janeUserData?.user.id,
    jwt,
    onMutationSuccess: () =>
      trackChangeBankAccountSuccess({ source: 'checkout' }),
  });

  const selectedBank =
    aeropayUserData &&
    aeropayUserData?.bank_accounts.find((bank) => bank.is_selected);

  const defaultBank = selectedBank || aeropayUserData?.bank_accounts[0];
  const hideBranding = Boolean(jane_pay_integration?.white_label);

  useEffect(() => {
    if (!isPending && isError) {
      toast.add({
        label: 'Could not update primary bank account',
        variant: 'error',
      });
    }
  }, [isPending, isError]);

  return (
    <>
      {isFetching || isPending ? (
        <Box>
          <Skeleton.Bone height={24} width={250} />
        </Box>
      ) : payByBankUI ? (
        <Flex flexDirection="column" gap={4}>
          <Flex gap={8} className={styles['headerContainer']}>
            <Flex
              className={styles['header']}
              width="100%"
              flexDirection="column-reverse"
              gap={8}
            >
              <Typography variant="h4-semibold" style={{ textWrap: 'nowrap' }}>
                Pay by bank
              </Typography>
              {brand_discounts_total && brand_discounts_total > 0 ? (
                <JaneGoldTag
                  label={`${formatCurrency(brand_discounts_total)} cash back`}
                />
              ) : null}
            </Flex>
            {!hideBranding && (
              <JanePayLogo
                data-testid="jane-pay-logo"
                width={98}
                height={24}
                ml="auto"
              />
            )}
          </Flex>
          <Typography>
            {defaultBank?.name
              ? `Pay instantly and securely with ${defaultBank.name}`
              : 'Link once and pay securely in seconds every time'}
          </Typography>
          {trustedByUI && (
            <Flex alignItems="center" gap={4}>
              <VerifiedIcon size="sm" color="grays-dark" />
              <Typography variant="small-body-regular">
                Trusted by 200,000+ shoppers
              </Typography>
            </Flex>
          )}
          {defaultBank && aeropayUserData.bank_accounts.length > 0 && (
            <Link
              onClick={() => {
                trackChangeBankAccountTap();
                setOpenSelectBankModal(true);
              }}
            >
              Change account
            </Link>
          )}
        </Flex>
      ) : defaultBank ? (
        <Flex alignItems="center" width="100%">
          <Box>
            {!hideBranding && <JanePayLogo width={98} height={20} />}
            <Typography mt={hideBranding ? 0 : 8} variant="body-bold">
              {defaultBank.name}
            </Typography>
          </Box>
          {aeropayUserData.bank_accounts.length > 0 && (
            <Box ml="auto">
              <Button.Icon
                variant="tertiary"
                icon={<EditIcon />}
                aria-label="edit primary account for jane pay"
                onClick={() => {
                  trackChangeBankAccountTap();
                  setOpenSelectBankModal(true);
                }}
              />
            </Box>
          )}
        </Flex>
      ) : (
        <Box>
          {!hideBranding && <JanePayLogo width={98} height={20} />}
          <Typography as="div" mt={hideBranding ? 0 : 8}>
            Pay by linking your bank account{!hideBranding ? '.' : ''}&nbsp;
            {!hideBranding && (
              <Link href="https://customer-support.iheartjane.com/s/article/Jane-Pay-FAQs">
                Learn more
              </Link>
            )}
          </Typography>
        </Box>
      )}
      {aeropayUserData &&
        aeropayUserData.bank_accounts.length > 0 &&
        openSelectBankModal && (
          <SelectBankAccountModal
            open={openSelectBankModal}
            onRequestClose={() => setOpenSelectBankModal(false)}
            onSave={selectAeropayBankAccount}
            bankAccounts={aeropayUserData.bank_accounts}
            onAddNewAccount={() => {
              setOpenSelectBankModal(false);
              setOpenBankLinkingModal(true);
              setAddNewAccount(true);
            }}
          />
        )}
      {openBankLinkingModal && (
        <BankLinkingModal
          addNewAccount={addNewAccount}
          hideJanePayBranding={hideBranding}
          open={openBankLinkingModal}
          onRequestClose={() => {
            setOpenBankLinkingModal(false);
            setAddNewAccount(false);
          }}
          location="checkout"
          isJanePay
          storePhoto={photo || undefined}
          isGold={shouldShowGold}
        />
      )}
    </>
  );
};
